"use client";

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CuratedStrategy,
  CuratedSignal,
} from "@/lib/api/mock-curated-strategies";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import Image from "next/image";
import {
  Lock,
  ScanEye,
  ShieldCheck,
  Zap,
  Globe,
  Crosshair,
  Workflow,
  Trophy,
  Crown,
  LucideIcon,
} from "lucide-react";

const XynthIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
  >
    <path d="M12 3L14.5 7.5H9.5L12 3Z" fill="currentColor" stroke="none" />
    <path d="M19 15L21.5 19.5H16.5L19 15Z" fill="currentColor" stroke="none" />
    <path d="M5 15L7.5 19.5H2.5L5 15Z" fill="currentColor" stroke="none" />
    <path d="M12 7.5V12" strokeLinecap="round" />
    <path d="M12 12L16.5 15" strokeLinecap="round" />
    <path d="M12 12L7.5 15" strokeLinecap="round" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

interface CuratedCardProps {
  strategy: CuratedStrategy;
}

export function CuratedCard({ strategy }: CuratedCardProps) {
  const [view, setView] = useState<"flow" | "terminal">("flow");

  // Auto-switch views every 16 seconds to let the animation breathe
  useEffect(() => {
    if (strategy.isComingSoon) return;

    const interval = setInterval(() => {
      setView((prev) => (prev === "flow" ? "terminal" : "flow"));
    }, 11000);
    return () => clearInterval(interval);
  }, [strategy.isComingSoon]);

  if (strategy.isComingSoon) {
    return null; // Hide "Coming Soon" placeholder as requested implicitly by "only show pinned cards" or just don't render it if we are unifying the feed.
    // Actually user said "pin the curated cards... and make the design more like community cards".
    // The previous code in Feed rendered a separate "Coming Soon" card.
    // In the new Feed code I removed the explicit "Coming Soon" card render, so I probably don't need to handle isComingSoon specially here unless the data has it.
    // But just in case:
  }

  return (
    <Link href={`/curated/${strategy.id}`}>
      <div className="group flex flex-col gap-3 cursor-pointer max-w-sm w-full mx-auto md:max-w-full md:mx-0 scale-[0.9] origin-top md:scale-100">
        {/* Visual Container - The "Card" part */}
        <div className="relative w-full aspect-[16/7] md:aspect-[16/10] bg-zinc-950 rounded-xl border border-zinc-800 overflow-hidden transition-all duration-300 group-hover:border-zinc-600 group-hover:shadow-md hover:shadow-[0_0_20px_rgba(34,211,238,0.15)]">
          {/* Background Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]"></div>

          <AnimatePresence mode="wait">
            {view === "flow" ? (
              <FlowDiagram key="flow" strategy={strategy} />
            ) : (
              <TerminalView key="terminal" signals={strategy.signals} />
            )}
          </AnimatePresence>

          {/* View Indicator Pills - Subtle at bottom center maybe? Or keep them but adjust z-index */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
            <div
              className={cn(
                "h-1 w-1 rounded-full transition-colors duration-300",
                view === "flow" ? "bg-zinc-200" : "bg-zinc-800"
              )}
            />
            <div
              className={cn(
                "h-1 w-1 rounded-full transition-colors duration-300",
                view === "terminal" ? "bg-zinc-200" : "bg-zinc-800"
              )}
            />
          </div>

          {/* Beta Badge (Top Left) */}
          <div className="absolute top-3 left-3 z-30">
            <Badge className="bg-purple-500/20 backdrop-blur-sm border-purple-500/30 text-purple-300 gap-1 pl-1.5 hover:bg-purple-500/30">
              <Zap className="w-3 h-3" /> Beta
            </Badge>
          </div>

          {/* Signals Badge (Bottom Right) */}
          <div className="absolute bottom-3 right-3 z-30">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm border border-white/10">
              <Zap className="w-3 h-3 text-white/80" />
              <span className="text-[10px] font-medium text-white/90 uppercase tracking-wider">
                Signals
              </span>
            </div>
          </div>
        </div>

        {/* Meta Info - Floating below */}
        <div className="flex gap-3 px-1">
          {/* Left: Avatar */}
          <div className="shrink-0 pt-1">
            <div className="relative w-8 h-8 rounded-full overflow-hidden border border-zinc-800 bg-zinc-900">
              <Image
                src="/images/avatars/avatar_7.svg"
                alt="SmartHedge"
                fill
                className="object-cover"
              />
            </div>
          </div>

          {/* Right: Title & Details */}
          <div className="flex flex-col min-w-0 gap-1.5">
            <h3 className="text-zinc-200 font-medium text-base leading-snug line-clamp-1 group-hover:text-white transition-colors">
              {strategy.title} Strategy
            </h3>

            {/* Sub-row */}
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="font-medium text-zinc-300">SmartHedge</span>

              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex items-center gap-0.5 text-amber-400 bg-amber-400/10">
                <Crown className="w-2 h-2" /> PRO
              </span>

              <span className="w-0.5 h-0.5 rounded-full bg-zinc-600" />

              <span className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Live
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Subcomponents                               */
/* -------------------------------------------------------------------------- */

function FlowDiagram({ strategy }: { strategy: CuratedStrategy }) {
  const [activeStep, setActiveStep] = useState(0);

  // Step Configuration
  const steps = useMemo(() => {
    const icons: Record<number, LucideIcon> = {
      0: ScanEye,
      1: ShieldCheck,
      2: Zap,
      3: Globe,
      4: Crosshair,
      5: Workflow,
      6: Trophy,
    };
    const colors: Record<number, string> = {
      0: "text-cyan-400",
      1: "text-purple-400",
      2: "text-yellow-400",
      3: "text-pink-400",
      4: "text-red-400",
      5: "text-orange-400",
      6: "text-emerald-400",
    };
    const glows: Record<number, string> = {
      0: "shadow-[0_0_15px_rgba(34,211,238,0.3)]",
      1: "shadow-[0_0_15px_rgba(192,132,252,0.3)]",
      2: "shadow-[0_0_15px_rgba(250,204,21,0.3)]",
      3: "shadow-[0_0_15px_rgba(244,114,182,0.3)]",
      4: "shadow-[0_0_15px_rgba(248,113,113,0.3)]",
      5: "shadow-[0_0_15px_rgba(251,146,60,0.3)]",
      6: "shadow-[0_0_15px_rgba(52,211,153,0.3)]",
    };

    return strategy.nodes.map((node, i) => ({
      ...node,
      Icon: icons[i] || ScanEye,
      colorClass: colors[i] || "text-zinc-400",
      glowClass: glows[i] || "",
    }));
  }, [strategy.nodes]);

  useEffect(() => {
    if (!steps.length) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 1500); // Slower step progression (1.5s)
    return () => clearInterval(interval);
  }, [steps.length]);

  // Layout Coordinates - dynamically generated based on node count
  const pos = useMemo(() => {
    const count = steps.length;
    if (count === 0) return [];

    // Distribute nodes evenly across the width
    const startX = 8;
    const endX = 92;
    const spacing = count > 1 ? (endX - startX) / (count - 1) : 0;

    return steps.map((_, i) => ({
      x: startX + spacing * i,
      y: i % 2 === 0 ? 65 : 25, // Alternate between bottom and top
    }));
  }, [steps.length]);

  // SVG Path - dynamically generated based on actual nodes
  const pathD = useMemo(() => {
    if (pos.length < 2) return "";

    let path = `M ${pos[0].x} ${pos[0].y}`;
    const r = 4; // corner radius (responsive to viewBox 0-100)

    for (let i = 1; i < pos.length; i++) {
      const prev = pos[i - 1];
      const curr = pos[i];

      const midX = (prev.x + curr.x) / 2;
      const yDist = curr.y - prev.y;
      const yDir = yDist > 0 ? 1 : -1;

      // 1. Horizontal to first turn
      path += ` L ${midX - r} ${prev.y}`;

      // 2. First turn (Horizontal -> Vertical)
      path += ` Q ${midX} ${prev.y}, ${midX} ${prev.y + yDir * r}`;

      // 3. Vertical segment
      path += ` L ${midX} ${curr.y - yDir * r}`;

      // 4. Second turn (Vertical -> Horizontal)
      path += ` Q ${midX} ${curr.y}, ${midX + r} ${curr.y}`;

      // 5. Horizontal to destination
      path += ` L ${curr.x} ${curr.y}`;
    }
    return path;
  }, [pos]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center select-none"
    >
      <div className="relative w-full h-full">
        {/* Connections Layer */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="gradientStroke"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="transparent" />
              <stop offset="20%" stopColor="rgba(255,255,255,0.1)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
              <stop offset="80%" stopColor="rgba(255,255,255,0.1)" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Base Path - Thicker and more visible */}
          <path
            d={pathD}
            fill="none"
            stroke="#3f3f46" // zinc-700
            strokeWidth="3"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            className="opacity-50"
          />

          {/* Animated Pulse Path - Glowing */}
          <motion.path
            d={pathD}
            fill="none"
            stroke="url(#gradientStroke)"
            strokeWidth="3"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            filter="url(#glow)"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: [0, 1],
              opacity: [0, 1, 0],
              strokeDashoffset: [0, -200],
            }}
            transition={{
              duration: 4, // Slower pulse
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </svg>

        {/* Nodes Layer */}
        {steps.map((step, index) => {
          const p = pos[index];
          const isActive = index === activeStep;

          return (
            <div
              key={step.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
            >
              <div className="relative group">
                {/* Glow Effect */}
                <div
                  className={cn(
                    "absolute inset-0 rounded-xl bg-current opacity-0 transition-opacity duration-500 blur-md",
                    isActive ? "opacity-40" : "opacity-0",
                    step.colorClass
                  )}
                />

                {/* Icon Container */}
                <motion.div
                  animate={{
                    scale: isActive ? 1.25 : 1,
                    backgroundColor: isActive ? "#18181b" : "#09090b", // zinc-900 vs zinc-950
                    borderColor: isActive ? "#71717a" : "#27272a", // zinc-500 vs zinc-800
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={cn(
                    "relative flex h-10 w-10 items-center justify-center rounded-xl border transition-all duration-300 z-10",
                    isActive ? step.glowClass : "shadow-none"
                  )}
                >
                  <step.Icon
                    className={cn(
                      "w-5 h-5 transition-all duration-300",
                      isActive
                        ? step.colorClass
                        : "text-zinc-600 grayscale-[0.5]"
                    )}
                  />

                  {/* Pulse Ring for Active */}
                  {isActive && (
                    <motion.div
                      initial={{ scale: 1, opacity: 1 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className={cn(
                        "absolute inset-0 rounded-xl border opacity-0",
                        step.colorClass.replace("text-", "border-")
                      )}
                    />
                  )}
                </motion.div>

                {/* Minimal Tooltip/Label on Hover or Active */}
                <div
                  className={cn(
                    "absolute left-1/2 -translate-x-1/2 top-full mt-2 pointer-events-none transition-all duration-300 z-20",
                    isActive
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2"
                  )}
                >
                  <div className="px-2 py-1 rounded-md bg-zinc-900/95 border border-zinc-800 text-[10px] font-medium text-zinc-300 shadow-xl backdrop-blur-sm whitespace-nowrap max-w-[100px] truncate">
                    {(() => {
                      const label = step.label.includes(":")
                        ? step.label.split(":")[1].split("(")[0].trim()
                        : step.label;
                      const words = label.split(" ").slice(0, 2).join(" ");
                      return words.length > 15
                        ? words.slice(0, 15) + "..."
                        : words;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function TerminalView({ signals }: { signals: CuratedSignal[] }) {
  // Logic to simulate a scanning process
  // We shuffle or just slice signals to make it look dynamic each time
  const [displayedSignals, setDisplayedSignals] = useState<CuratedSignal[]>([]);

  useEffect(() => {
    let timeoutIds: NodeJS.Timeout[] = [];

    // Reset
    setDisplayedSignals([]);

    // Animate in one by one
    signals.forEach((signal, index) => {
      const timeout = setTimeout(() => {
        setDisplayedSignals((prev) => [...prev, signal]);
      }, index * 600); // 600ms per signal
      timeoutIds.push(timeout);
    });

    return () => timeoutIds.forEach(clearTimeout);
  }, [signals]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 p-6 font-mono text-xs overflow-hidden"
    >
      <div className="absolute top-2 right-3 flex items-center gap-2">
        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
        <span className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">
          {signals.filter((s) => s.status === "found").length} Active Trades
        </span>
      </div>

      <div className="space-y-2 mt-4">
        {displayedSignals.map((signal, i) => (
          <motion.div
            key={`${signal.ticker}-${i}`}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="flex items-center gap-3 border-b border-zinc-800/50 pb-1"
          >
            <span
              className={cn(
                "font-bold min-w-[12px]",
                signal.status === "found" ? "text-green-500" : "text-red-500/50"
              )}
            >
              {signal.status === "found" ? "✓" : "✗"}
            </span>

            <div className="flex gap-2 items-baseline">
              <span
                className={cn(
                  "font-bold",
                  signal.status === "found" ? "text-zinc-200" : "text-zinc-600"
                )}
              >
                {signal.ticker} {signal.strike}
                {signal.option_type === "CALL" ? "C" : "P"}
              </span>
              <span className="text-[10px] text-zinc-600">{signal.expiry}</span>
            </div>

            <div className="ml-auto">
              {signal.status === "found" ? (
                <span className="text-[9px] bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded">
                  MATCH
                </span>
              ) : (
                <span className="text-[9px] text-zinc-700 truncate max-w-[80px]">
                  {signal.reason}
                </span>
              )}
            </div>
          </motion.div>
        ))}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} />
      </div>

      {/* Scanning Gradient Overlay */}
      <motion.div
        initial={{ top: "-10%" }}
        animate={{ top: "110%" }}
        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
        className="absolute left-0 right-0 h-[15%] bg-gradient-to-b from-transparent via-green-500/10 to-transparent pointer-events-none"
      />
    </motion.div>
  );
}
